import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Trophy,
  Layers,
  PlayCircle,
  MessageSquare,
  Users,
  IdCard,
  User,
  Shield,
  Award,
  HelpCircle,
  Swords,
  Rss,
  Bell,
  X,
  LogOut,
} from "lucide-react";
import { LOGO_MARK, TAGLINE } from "../lib/brand";
import { LogoMark } from "./Logo";
import { useNotifications } from "../context/NotificationContext";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { unreadCount, toast, setToast } = useNotifications();
  const navigate = useNavigate();

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/tournaments", label: "Wettkämpfe", icon: Trophy, testid: "nav-tournaments" },
    { to: "/live", label: "Live Matches", icon: PlayCircle, testid: "nav-live" },
    { to: "/boards", label: "Ranglisten · Boards", icon: Layers, testid: "nav-boards" },
    { to: "/tactics", label: "Tactical Builder", icon: Swords, testid: "nav-tactics" },
    { to: "/social", label: "Social Feed", icon: Rss, testid: "nav-social" },
    { to: "/chat", label: "Chat", icon: MessageSquare, testid: "nav-chat" },
    { to: "/friends", label: "Freunde", icon: Users, testid: "nav-friends" },
    { to: "/achievements", label: "Erfolge", icon: Award, testid: "nav-achievements" },
    { to: "/athlete-id", label: "Sportlerausweis", icon: IdCard, testid: "nav-athleteid" },
    { to: "/support", label: "Support / Tickets", icon: HelpCircle, testid: "nav-support" },
    { to: "/profile", label: "Profil", icon: User, testid: "nav-profile" },
  ];

  if (user?.role === "admin") {
    nav.push({ to: "/admin", label: "Admin Dashboard", icon: Shield, testid: "nav-admin" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="logo-link">
            <LogoMark src={LOGO_MARK} size="w-7 h-7" />
            <span className="brand-text text-xl">NORDGAMING</span>
            <span className="hidden sm:inline font-mono text-[10px] text-white/40 uppercase tracking-[0.2em] ml-2">
              · Portal
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-8 h-8 rounded-sm object-cover border border-white/10"
              />
            ) : (
              <div className="w-8 h-8 rounded-sm bg-neon-gradient-soft border border-primary/30 flex items-center justify-center font-mono text-xs text-primary">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            
            <button
              onClick={() => navigate("/notifications")}
              className="relative btn-ghost"
              title="Benachrichtigungen"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary animate-pulse" />
              )}
            </button>

            <div className="hidden md:block text-right ml-2">
              <div className="text-sm font-medium leading-none">{user?.name}</div>
              <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
                {user?.member_id}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="btn-ghost"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-8 py-8 grid grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="hidden lg:block col-span-3">
          <nav className="sticky top-24 space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 px-3 pb-3">
              Navigation
            </div>
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/dashboard"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors border-l-2 ${
                    isActive
                      ? "bg-neon-gradient-soft text-white border-primary"
                      : "text-white/70 hover:text-white hover:bg-white/5 border-transparent"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{n.label}</span>
              </NavLink>
            ))}
            <div className="pt-8 px-3 text-[10px] font-mono uppercase tracking-[0.25em] text-white/30 brand-text">
              {TAGLINE}
            </div>
          </nav>
        </aside>

        {/* Mobile nav */}
        <nav className="lg:hidden col-span-12 -mx-4 px-4 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/dashboard"}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-sm text-xs whitespace-nowrap border ${
                    isActive
                      ? "bg-neon-gradient-soft text-primary border-primary/40"
                      : "text-white/70 border-border hover:border-primary/40"
                  }`
                }
              >
                <n.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <main className="col-span-12 lg:col-span-9">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between text-xs text-white/40">
          <div className="font-mono uppercase tracking-[0.2em] flex items-center gap-2">
            <LogoMark src={LOGO_MARK} size="w-4 h-4" />
            © Nordgaming e.V. · 2026
          </div>
          <div className="font-mono uppercase tracking-[0.2em]">v0.2 · {TAGLINE}</div>
        </div>
      </footer>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-20 right-4 md:right-8 z-50 animate-in fade-in slide-in-from-top-5 max-w-sm w-full bg-bg border border-primary/30 shadow-lg shadow-primary/10 rounded-sm p-4 flex gap-4 items-start">
          <div className="mt-0.5 text-primary">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-white">{toast.title}</h4>
            <p className="text-xs text-white/70 mt-1">{toast.message}</p>
          </div>
          <button onClick={() => setToast(null)} className="text-white/50 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
